import produce from 'immer';

import {
  LastSyncedUpstreamValue,
  PrimarySample,
  StructuredTable,
  StructuredTableColumn,
  StructuredTableRow,
  StructuredTableRowUpdater,
  StructuredTableRowUpdaterModel,
} from 'notebook/structured-tables/StructuredTableConstants';
import fetchAllSamples from 'notebook/structured-tables/util/fetchAllSamples';
import getLastSyncedUpstreamValueForColumn from 'notebook/structured-tables/util/getLastSyncedUpstreamValueForColumn';
import {resolveRowDelegateColumn} from 'notebook/structured-tables/util/resolveRowDelegateColumn';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {areTypedValuesEquivalent} from 'notebook/tables/typed-values/DataTypes';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {omit} from 'util/omitKey';

// Keep in sync with LAST_SYNCED_UPSTREAM_VALUES_SCHEMA in benchling/notebook/structured_tables/validation.py
export const SUPPORTED_COLUMN_TYPES = [
  TableColumnType.TAG_VALUE,
  TableColumnType.ALIAS,
  TableColumnType.UNIT_SELECTOR,
  TableColumnType.UNIT_REFERENCE,
  TableColumnType.MEASUREMENT_AMOUNT,
  TableColumnType.INGREDIENT_CATALOG_ID,
  TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT,
  TableColumnType.EXISTING_BIOENTITY,
  TableColumnType.CONTAINERS,
  TableColumnType.INGREDIENT_NOTES,
  TableColumnType.PROCESS_OUTPUT,
  TableColumnType.PROCESS_TASK,
  TableColumnType.PROCESS_TASK_ASSIGNEE,
  TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE,
  TableColumnType.PROCESS_TASK_STATUS,
  TableColumnType.CUSTOM,
  TableColumnType.STUDIES,
  TableColumnType.PROCEDURE_CONDITION_REPLICATES,
  TableColumnType.STRING,
];

// Column types to preserve last synced upstream values on refresh. These are column types that have no
// external source of truth and will only be updated on table submission.
// On refresh, any unsubmitted updates to the cells will be wiped away.
export const COLUMN_TYPES_PRESERVE_LAST_SYNCED_UPSTREAM_VALUES = [
  TableColumnType.CUSTOM,
  TableColumnType.PROCEDURE_CONDITION_REPLICATES,
];

/**
 * Calculate and insert the appropriate lastSyncedUpstreamValues for the updatedRow.  If the value
 * for a column hasn't changed, then keep the old submittedAt time.
 *
 * @param columns - the columns in the table
 * @param originalRow - the originalRow with the old values of lastSyncedUpstreamValue
 * @param updatedRow - the new row which does not yet have lastSyncedUpstreamValues populated
 */
export function updateLastSyncedUpstreamValues<TRowMetadata>(
  columns: Array<StructuredTableColumn>,
  originalRow: StructuredTableRow<TRowMetadata> | null,
  updatedRow: StructuredTableRow<TRowMetadata>
): StructuredTableRow<TRowMetadata> {
  const lastSyncedUpstreamValues = originalRow?.lastSyncedUpstreamValues ?? [];
  const updatedSubmittedAt = new Date().toISOString();
  const updatedLastSyncedUpstreamValues: Array<LastSyncedUpstreamValue> = columns
    .map((column, columnIndex) => {
      if (!SUPPORTED_COLUMN_TYPES.includes(column.columnType)) {
        return null;
      }
      const originalLastSyncedValue = getLastSyncedUpstreamValueForColumn(lastSyncedUpstreamValues, column);
      const updatedTypedValue = updatedRow.cells[columnIndex].typedValue ?? null;
      const originalTypedValue = originalLastSyncedValue?.value ?? null;
      if (originalRow != null && areTypedValuesEquivalent(originalTypedValue, updatedTypedValue)) {
        return originalLastSyncedValue;
      } else if (
        originalRow != null &&
        updatedTypedValue?.type === FieldType.TEXT &&
        originalRow.cells[columnIndex].link
      ) {
        // If the plaintext typed value has changed outside of the editor context, clear its link
        updatedRow.cells[columnIndex].link = null;
      }
      return {
        columnType: column.columnType,
        columnGroup: column.columnGroup ?? null,
        fieldId: column.schemaFieldApiId ?? null,
        columnProvenanceUuid: column.provenanceUuid,
        value: updatedTypedValue,
        submittedAt: updatedSubmittedAt,
      };
    })
    .filter((x): x is LastSyncedUpstreamValue => x != null);
  return {
    ...updatedRow,
    lastSyncedUpstreamValues: updatedLastSyncedUpstreamValues,
  };
}

/**
 * Updates a StructuredTable by using the given StructuredTableRowUpdater to fetch any samples
 * that it knows how to fetch and then update the given row for that sample.  This function is called
 * during the RefreshTableData flow and allows various StructuredTableAdapters to specify how they
 * want to update entries that aren't just the standard Bioentities or Storables and their tag values.
 *
 * @param samplesByRowId
 * @param rowUpdater
 * @param structuredTable
 */
export async function updateStructuredTableUsingRowUpdater<TTableMetadata, TRowMetadata>({
  samplesByRowId,
  rowUpdaters,
  structuredTable,
}: {
  samplesByRowId: {[rowId: string]: Array<PrimarySample>};
  rowUpdaters: Array<StructuredTableRowUpdater<StructuredTableRowUpdaterModel, TRowMetadata>>;
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
}): Promise<StructuredTable<TTableMetadata, TRowMetadata>> {
  const updatedRows = await Promise.all(
    structuredTable.rows.map(async (row) => {
      const {rowId} = row;
      if (rowId == null || samplesByRowId[rowId] == null) {
        return Promise.resolve(row);
      }
      const updatedSamples = await fetchAllSamples(samplesByRowId[rowId], rowUpdaters);

      return updatedSamples.reduce((updatedRow, {updates, columnGroup}) => {
        const newRow = updates.reduce(
          (partiallyUpdatedSampleRow, {rowUpdater, updatedModel}) =>
            // Row Updaters will handle resolving delegated columns when applicable within updateRow
            rowUpdater.updateRow(
              updatedModel,
              structuredTable.columns,
              partiallyUpdatedSampleRow,
              columnGroup
            ),
          updatedRow
        );
        const resolvedColumns = structuredTable.columns.map((column) =>
          resolveRowDelegateColumn(column, newRow, structuredTable.delegatedColumns)
        );
        return {
          ...updateLastSyncedUpstreamValues(resolvedColumns, updatedRow, newRow),
          lastSyncedArchivePurpose: newRow.lastSyncedArchivePurpose,
        };
      }, row);
    })
  );

  const postProcessedRows = normalizeMergeCellRanges(updatedRows);

  return {
    ...structuredTable,
    rows: postProcessedRows,
  };
}

/**
 * Normalize the results of updating the table via the row updater with any merged cell ranges on the table.
 * The row updater will write live data to cells, ignoring merge cell ranges. This method inspects each merge
 * cell range and takes one of two actions based on whether data in each cell in the range matches the
 * "primary" (top left) cell...
 * 1. (Data matches): Replace non-primary cells with an empty cell
 * 2. (Data does not match): Remove the merge so that all cells will be visible
 *
 * Note: This method is compatible with 2D merge ranges, even though we only support single-column ranges in
 * structured tables.
 *
 * @param rows The structured table rows
 * @returns A version of the input with normalized merge cell range(s)
 */
function normalizeMergeCellRanges<TRowMetadata>(
  rows: Array<StructuredTableRow<TRowMetadata>>
): Array<StructuredTableRow<TRowMetadata>> {
  return produce(rows, (draftRows) => {
    draftRows.forEach((row, rowIdx) => {
      row.cells.forEach((cell, colIdx) => {
        if (cell.mergeRowSpan != null || cell.mergeColSpan != null) {
          const shouldUnmerge = draftRows
            .slice(rowIdx, rowIdx + (cell.mergeRowSpan ?? 1))
            .some(
              (row) =>
                row.isFinalized &&
                (row.lastSyncedArchivePurpose != null ||
                  row.cells
                    .slice(colIdx, colIdx + (cell.mergeColSpan ?? 1))
                    .some(
                      (mergedCell) =>
                        mergedCell.text !== cell.text ||
                        !areTypedValuesEquivalent(mergedCell.typedValue ?? null, cell.typedValue ?? null)
                    ))
            );

          if (shouldUnmerge) {
            draftRows[rowIdx].cells[colIdx] = omit(
              draftRows[rowIdx].cells[colIdx],
              'mergeRowSpan',
              'mergeColSpan'
            );
          } else {
            for (let i = rowIdx; i < rowIdx + (cell.mergeRowSpan ?? 1); i++) {
              for (let j = colIdx; j < colIdx + (cell.mergeColSpan ?? 1); j++) {
                if (i !== rowIdx || j !== colIdx) {
                  draftRows[i].cells[j] = {text: ''};
                }
              }
            }
          }
        }
      });
    });
  });
}
